import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { Modal, Table, Tooltip } from "antd";
import {
  ShoppingOutlined,
  ClockCircleOutlined,
  CheckCircleFilled,
  WarningFilled,
  LinkOutlined,
  MutedOutlined,
} from "@ant-design/icons";

import "./index.css";
import { getApi, errorMessage } from "../../helpers";
import { StoreContext } from "../../context";
import Badge from "../Badge";
import OrderSidebar from "../../screens/Orders/OrderSidebar";

const TIMER_DURATION = 15 * 60 * 1000; // 15 minutes
const POLL_INTERVAL = 30 * 1000;
// Generous enough for a slow store connection fetching up to 200 orders,
// still well inside the 30s gap between checks.
const POLL_TIMEOUT = 20 * 1000;
const MIN_GAP_ON_RETURN = 10 * 1000;
// One failed check is usually a blip (e.g. waking from sleep); this many in a
// row (~90s without updates) means live orders really aren't coming through.
const FAILURES_BEFORE_WARNING = 3;

// One shared context: browsers cap how many can exist, and they start
// "suspended" until the user interacts with the page (autoplay policy).
let audioContext = null;
function getAudioContext() {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return null;
  if (!audioContext) audioContext = new AudioCtx();
  return audioContext;
}

function playNotificationSound() {
  try {
    const ctx = getAudioContext();
    if (!ctx || ctx.state !== "running") return;

    const playTone = (freq, start, dur, vol = 0.7) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "square";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(vol, start);
      gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
      osc.start(start);
      osc.stop(start + dur);
    };

    const t = ctx.currentTime;
    playTone(880, t, 0.12);
    playTone(1320, t + 0.15, 0.12);
    playTone(880, t + 0.3, 0.12);
    playTone(1320, t + 0.45, 0.12);
    playTone(1760, t + 0.6, 0.25);
  } catch (e) {
    // Audio not available
  }
}

function getTodayRange() {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  const dateStr = `${yyyy}-${mm}-${dd}`;
  return { start_date: dateStr, end_date: dateStr };
}

function formatTimer(remaining) {
  const clamped = Math.max(0, remaining);
  const mins = Math.floor(clamped / 60000);
  const secs = Math.floor((clamped % 60000) / 1000);
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

export default function LiveOrderChip() {
  const { storeId } = useContext(StoreContext);
  const [orders, setOrders] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [orderSidebarVisible, setOrderSidebarVisible] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  // Map<order_id, { arrivalTime, orderStatus }> — only unacknowledged orders
  const [pendingOrders, setPendingOrders] = useState(new Map());
  // Set<order_id> — orders the admin has viewed
  const [viewedOrders, setViewedOrders] = useState(new Set());

  // Ticking clock for timer display (updates every second when pending > 0)
  const [now, setNow] = useState(Date.now());

  const [notUpdating, setNotUpdating] = useState(false);
  const [soundBlocked, setSoundBlocked] = useState(false);

  const knownOrderIds = useRef(null);
  const consecutiveFailures = useRef(0);
  // Bumped whenever the store changes (or the chip unmounts), so answers to
  // checks sent for the previous store are ignored.
  const pollGeneration = useRef(0);
  const inFlightGeneration = useRef(null);
  const lastCheckAt = useRef(0);

  const fetchTodayOrders = async () => {
    const generation = pollGeneration.current;
    // Never run two checks at once; a stuck one would race the next.
    if (inFlightGeneration.current === generation) return;
    inFlightGeneration.current = generation;
    lastCheckAt.current = Date.now();

    const payload = { ...getTodayRange(), limit: 200, page: 0 };
    if (storeId) payload.store_id = storeId;

    const res = await getApi(payload, "getOrders", "ecomm/", {}, {
      silent: true,
      timeout: POLL_TIMEOUT,
    });
    if (inFlightGeneration.current === generation) inFlightGeneration.current = null;
    if (generation !== pollGeneration.current) return;

    if (!res || !res.data) {
      consecutiveFailures.current += 1;
      if (consecutiveFailures.current === FAILURES_BEFORE_WARNING) {
        setNotUpdating(true);
        errorMessage("Live orders aren't updating. Check your connection.");
      }
      return;
    }
    consecutiveFailures.current = 0;
    setNotUpdating(false);

    const fetched = res.data;
    const fetchedIds = new Set(fetched.map((o) => o.order_id));

    if (knownOrderIds.current === null) {
      knownOrderIds.current = fetchedIds;
      setOrders(fetched);
      return;
    }

    const newIds = [];
    fetchedIds.forEach((id) => {
      if (!knownOrderIds.current.has(id)) newIds.push(id);
    });

    if (newIds.length > 0) {
      const arrivalTime = Date.now();
      setPendingOrders((prev) => {
        if (prev.size === 0) playNotificationSound();
        const next = new Map(prev);
        newIds.forEach((id) => {
          const order = fetched.find((o) => o.order_id === id);
          next.set(id, { arrivalTime, orderStatus: order?.order_status });
        });
        return next;
      });
      knownOrderIds.current = fetchedIds;
    }

    // Auto-acknowledge orders whose status has changed since they were flagged pending
    setPendingOrders((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Map(prev);
      prev.forEach(({ orderStatus }, id) => {
        const current = fetched.find((o) => o.order_id === id);
        if (current && current.order_status !== orderStatus) {
          next.delete(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });

    setOrders(fetched);
  };

  // Polling for new orders
  useEffect(() => {
    pollGeneration.current += 1;
    knownOrderIds.current = null;
    consecutiveFailures.current = 0;
    setNotUpdating(false);
    setPendingOrders(new Map());
    setViewedOrders(new Set());
    fetchTodayOrders();
    const intervalId = setInterval(fetchTodayOrders, POLL_INTERVAL);

    // Keeps polling while hidden (for the new-order sound), but browsers slow
    // hidden-tab timers down, so check straight away on coming back.
    const onVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      // Rapid tab switching shouldn't fire a check every time.
      if (Date.now() - lastCheckAt.current < MIN_GAP_ON_RETURN) return;
      fetchTodayOrders();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      pollGeneration.current += 1;
    };
  }, [storeId]);

  // Sound only works once the page has been interacted with since loading;
  // any click or key press unlocks it.
  useEffect(() => {
    const ctx = getAudioContext();
    if (!ctx) return undefined;
    const sync = () => setSoundBlocked(ctx.state !== "running");
    const unlock = () => {
      if (ctx.state === "suspended") ctx.resume().then(sync).catch(sync);
    };
    sync();
    ctx.addEventListener("statechange", sync);
    document.addEventListener("pointerdown", unlock);
    document.addEventListener("keydown", unlock);
    return () => {
      ctx.removeEventListener("statechange", sync);
      document.removeEventListener("pointerdown", unlock);
      document.removeEventListener("keydown", unlock);
    };
  }, []);

  // 1-second tick when there are pending orders
  useEffect(() => {
    if (pendingOrders.size === 0) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [pendingOrders.size > 0]);

  // Derived timer state
  const timerInfo = useMemo(() => {
    if (pendingOrders.size === 0) return null;

    let mostUrgent = Infinity;
    let overdueCount = 0;

    pendingOrders.forEach(({ arrivalTime }) => {
      const remaining = TIMER_DURATION - (now - arrivalTime);
      if (remaining < mostUrgent) mostUrgent = remaining;
      if (remaining <= 0) overdueCount++;
    });

    return {
      text: formatTimer(mostUrgent),
      overdue: overdueCount > 0,
      overdueCount,
      pendingCount: pendingOrders.size,
    };
  }, [pendingOrders, now]);

  const acknowledgeOrder = (orderId) => {
    setPendingOrders((prev) => {
      const next = new Map(prev);
      next.delete(orderId);
      return next;
    });
    setViewedOrders((prev) => new Set([...prev, orderId]));
  };

  const handleOrderClick = (id) => {
    acknowledgeOrder(id);
    setModalOpen(false);
    setSelectedOrderId(id);
    setOrderSidebarVisible(true);
  };

  const hasPending = pendingOrders.size > 0;
  const isOverdue = timerInfo?.overdue;

  const columns = [
    {
      title: "Order ID",
      dataIndex: "order_id",
      key: "order_id",
      width: 90,
      render: (id) => (
        <a className="live-order-id-link" onClick={() => handleOrderClick(id)}>
          {id} <LinkOutlined />
        </a>
      ),
    },
    ...(!storeId
      ? [
          {
            title: "Store",
            dataIndex: "store_name",
            key: "store_name",
            width: 130,
            render: (text) => text || "—",
          },
        ]
      : []),
    {
      title: "Customer Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Mobile",
      dataIndex: "mobile",
      key: "mobile",
      width: 120,
    },
    {
      title: "Order Status",
      dataIndex: "order_status",
      key: "order_status",
      align: "center",
      render: (text) => <Badge orderStatus={text} />,
    },
    {
      title: "Payment Status",
      dataIndex: "payment_status",
      key: "payment_status",
      align: "center",
      render: (text) => <Badge paymentStatus={text} />,
    },
    {
      title: "Response",
      key: "response",
      width: 110,
      align: "center",
      render: (_, record) => {
        const pendingEntry = pendingOrders.get(record.order_id);
        if (pendingEntry) {
          const remaining = TIMER_DURATION - (now - pendingEntry.arrivalTime);
          const overdue = remaining <= 0;
          return (
            <Tooltip title="Click Order ID to acknowledge">
              <span
                className={`live-order-timer-cell ${
                  overdue ? "live-order-timer-cell--overdue" : ""
                }`}
              >
                <ClockCircleOutlined />
                <span>{formatTimer(remaining)}</span>
              </span>
            </Tooltip>
          );
        }
        if (viewedOrders.has(record.order_id)) {
          return (
            <span className="live-order-viewed-cell">
              <CheckCircleFilled />
              <span>Viewed</span>
            </span>
          );
        }
        return <span style={{ color: "#9ca3af" }}>—</span>;
      },
    },
  ];

  return (
    <>
      <div
        className={`live-order-chip${hasPending ? " live-order-chip--new" : ""}${
          isOverdue ? " live-order-chip--overdue" : ""
        }${notUpdating ? " live-order-chip--not-updating" : ""}`}
        onClick={() => setModalOpen(true)}
        title={
          notUpdating
            ? "Live orders aren't updating. Check your connection."
            : "Click to view today's orders"
        }
      >
        {hasPending && <span className="live-order-chip__pulse" />}
        <span className="live-order-chip__dot" />
        <ShoppingOutlined className="live-order-chip__icon" />
        <span className="live-order-chip__label">Live Orders</span>
        <span className="live-order-chip__count">{orders.length}</span>
        {notUpdating && <span className="live-order-chip__status">Not updating</span>}
        {soundBlocked && (
          <Tooltip title="Click anywhere to enable new-order sounds">
            <MutedOutlined className="live-order-chip__muted" />
          </Tooltip>
        )}

        {timerInfo && (
          <>
            <span className="live-order-chip__divider" />
            <ClockCircleOutlined className="live-order-chip__timer-icon" />
            <span
              className={`live-order-chip__timer ${
                isOverdue ? "live-order-chip__timer--overdue" : ""
              }`}
            >
              {timerInfo.text}
            </span>
            <span className="live-order-chip__pending">
              {timerInfo.pendingCount} pending
            </span>
          </>
        )}
      </div>

      <Modal
        title={
          <div className="live-order-modal-title">
            <ShoppingOutlined style={{ color: "#6b7280" }} />
            <span>Today's Live Orders</span>
            <span className="live-order-modal-count">
              {orders.length} orders
            </span>
            {hasPending && (
              <span className="live-order-modal-pending">
                {isOverdue && <WarningFilled />}
                {pendingOrders.size} pending response
              </span>
            )}
          </div>
        }
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={storeId ? 1040 : 1140}
        destroyOnClose={false}
      >
        <Table
          dataSource={orders}
          columns={columns}
          rowKey="order_id"
          size="small"
          pagination={{ pageSize: 15, showSizeChanger: false }}
          rowClassName={(record) => {
            const pendingEntry = pendingOrders.get(record.order_id);
            if (pendingEntry) {
              const remaining =
                TIMER_DURATION - (now - pendingEntry.arrivalTime);
              if (remaining <= 0) return "live-order-row--overdue";
              return "live-order-row--pending";
            }
            return "";
          }}
          scroll={{ x: 800 }}
        />
      </Modal>

      {orderSidebarVisible && (
        <OrderSidebar
          visible={orderSidebarVisible}
          onClose={() => setOrderSidebarVisible(false)}
          order_id={selectedOrderId}
        />
      )}
    </>
  );
}
